import { html, LitElement, nothing } from 'lit'
import { customElement, query } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { overviewSlideIn } from './overview-slide-in.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { type CSESlideIn } from '@cloudsuite/mosaic-elements'
import { draftRmaContext, enableRmaCommentContext, NotFetchedYet, updateRmaCommentContext } from 'components/ordering/rma/components/context/rma-context.js'
import { RmaDraftDTO } from 'components/ordering/rma/api/dtos/rma-draft-dto.js'
import { RmaLineDTO } from 'components/ordering/rma/api/dtos/rma-line-dto.js'
import 'components/ordering/rma/components/product-row/product-row.js'
import 'components/ordering/rma/components/rma/discard-button.js'
import 'components/ordering/rma/components/rma/submit-button.js'

const translations = {
  header: () => t('header', { ns: 'csc-rma-overview-slide-in', default: 'Return request overview' }),
  commentLabel: () => t('comment', { ns: 'csc-rma-overview-slide-in', default: 'Comment' }),
  optional: () => t('optional', { ns: 'csc-rma-overview-slide-in', default: 'Optional' }),
  empty: () => t('empty', { ns: 'csc-rma-overview-slide-in', default: 'No products selected' }),
}

@customElement('csc-rma-overview-slide-in')
export class CSCRmaOverviewSlideIn extends LitElement {
  static override styles = [hostStyle, overviewSlideIn]

  @query('cse-slide-in')
  private slideIn!: CSESlideIn

  @consume({ context: draftRmaContext, subscribe: true })
  private draftRma: RmaDraftDTO | null | NotFetchedYet = new NotFetchedYet()

  @consume({ context: enableRmaCommentContext, subscribe: true })
  private enableRmaComment = false

  @consume({ context: updateRmaCommentContext, subscribe: true })
  private updateRmaComment: ((comment: string | null) => Promise<void>) | null = null

  private get lines(): RmaLineDTO[] {
    return this.draftRma instanceof RmaDraftDTO ? this.draftRma.lines : []
  }

  private get comment(): string {
    return this.draftRma instanceof RmaDraftDTO ? this.draftRma.comment ?? '' : ''
  }

  private async onCommentChange(event: Event): Promise<void> {
    const value: string = (event.target as HTMLTextAreaElement).value
    await this.updateRmaComment?.(value === '' ? null : value)
  }

  private renderComment() {
    if (!this.enableRmaComment) return nothing
    return html`
      <cse-form-field class="overview__comment" data-test="overview-comment">
        <cse-label slot="label" optional>
          ${translations.commentLabel()}
          <span slot="optional">${translations.optional()}</span>
        </cse-label>
        <cse-textarea
          name="comment"
          value=${this.comment}
          @change=${(event: Event) => this.onCommentChange(event)}
        ></cse-textarea>
      </cse-form-field>
    `
  }

  public open() {
    void this.slideIn?.open()
  }

  public close() {
    void this.slideIn?.close()
  }

  private renderContent() {
    const lines = this.lines
    if (lines.length === 0) {
      return html`<cse-text data-test="overview-empty">${translations.empty()}</cse-text>`
    }
    return html`
      <cse-accordion-group>
        ${lines.map((line) => html`<csc-rma-product-row .lineData=${line}></csc-rma-product-row>`)}
      </cse-accordion-group>
      ${this.renderComment()}
    `
  }

  override render() {
    const hasLines = this.lines.length > 0
    return html`
      <cse-slide-in size="large">
        <div slot="header">${translations.header()}</div>
        ${this.renderContent()}
        <div slot="footer" class="overview__footer">
          <csc-rma-discard-button data-test="overview-discard"></csc-rma-discard-button>
          ${hasLines
            ? html`<csc-rma-submit-button data-test="overview-submit"></csc-rma-submit-button>`
            : nothing}
        </div>
      </cse-slide-in>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-overview-slide-in': CSCRmaOverviewSlideIn
  }
}
